'use client';

import { useState, useEffect } from 'react';

/* ───────────── INTERVIEW — first-party AI agentic interviewer ─────────────
   AI interviewer orb + candidate avatar (no faces), live transcription,
   auto-rotating AI insights, resolving to scorecard + verdict.
   All first-party: AI interviewer, live transcription, AI report. */

interface Line {
  s: 'AI' | 'CND';
  t: string;
}

interface Insight {
  label: string;
  lines: string[];
}

interface Score {
  k: string;
  n: number;
}

export function InterviewExp() {
  const [n, setN] = useState<number>(0);
  const [resolved, setResolved] = useState<boolean>(false);
  const [rot, setRot] = useState<number>(0);

  const LINES: Line[] = [
    { s: 'AI', t: 'Design a rate limiter for a high-traffic API.' },
    { s: 'CND', t: 'Sliding window in Redis — a sorted set of timestamps per client.' },
    { s: 'AI', t: 'And when Redis becomes the bottleneck?' },
    { s: 'CND', t: 'Local token bucket, async-sync to Redis. Trade exactness for throughput.' },
  ];

  const INSIGHTS: Insight[] = [
    { label: 'CANDIDATE BRIEF', lines: ['8 yrs · backend · distributed systems', 'Verified: assessment 91 · identity'] },
    { label: 'AI TAKE', lines: ['Strong grasp of consistency trade-offs.', 'Probe failure modes next.'] },
    { label: 'SUGGESTED', lines: ['How do you handle a Redis partition?', 'Where does the bucket drift?'] },
  ];

  const SCORES: Score[] = [
    { k: 'Technical depth', n: 5 },
    { k: 'Communication', n: 4 },
    { k: 'Problem solving', n: 5 },
    { k: 'Culture fit', n: 4 },
    { k: 'Leadership', n: 4 },
  ];

  useEffect(() => {
    const t = LINES.map((_, i) => setTimeout(() => setN(i + 1), 600 + i * 850));
    t.push(setTimeout(() => setResolved(true), 600 + LINES.length * 850 + 500));
    return () => t.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // rotate AI insights while interview is live
  useEffect(() => {
    if (resolved) return;
    const iv = setInterval(() => setRot(r => (r + 1) % INSIGHTS.length), 2300);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved]);

  const live = !resolved;
  const activeSpeaker: 'AI' | 'CND' = n > 0 ? LINES[n - 1].s : 'AI';
  const ins = INSIGHTS[rot];

  return (
    <div className="exp-split" style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: '14px', height: '100%' }}>
      {/* LEFT — room + transcript */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 }}>
        {/* tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {/* AI interviewer orb */}
          <div style={{ position: 'relative', height: '94px', borderRadius: 'var(--r-md)', overflow: 'hidden', border: '1px solid var(--border)',
            background: 'radial-gradient(120% 120% at 50% 35%, var(--spill), var(--surface))' }}>
            <AIOrb speaking={live && activeSpeaker === 'AI'} />
            <span className="mono" style={{ position: 'absolute', bottom: '7px', left: '9px', fontSize: '8px', color: 'var(--indigo-light)' }}>VHire AI · interviewer</span>
            {live && <span className="mono" style={{ position: 'absolute', top: '7px', right: '9px', fontSize: '8px', color: 'var(--crimson)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--crimson)', animation: 'spill-pulse 1.2s ease infinite' }}></span>REC
            </span>}
          </div>
          {/* candidate avatar */}
          <div style={{ position: 'relative', height: '94px', borderRadius: 'var(--r-md)', overflow: 'hidden', border: '1px solid var(--border)',
            background: 'radial-gradient(120% 120% at 50% 30%, var(--surface-raised), var(--surface))' }}>
            <Avatar speaking={live && activeSpeaker === 'CND'} />
            <span className="mono" style={{ position: 'absolute', bottom: '7px', left: '9px', fontSize: '8px', color: 'var(--text-3)' }}>Candidate</span>
            <span className="mono" style={{ position: 'absolute', top: '7px', right: '9px', fontSize: '8px', color: 'var(--text-2)' }}>12:48</span>
          </div>
        </div>

        {/* transcript */}
        <div style={{ flex: 1, borderRadius: 'var(--r-md)', border: '1px solid var(--border)', background: 'var(--surface)', padding: '12px 14px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ marginBottom: '9px' }}>
            {live
              ? <span className="mono" style={{ fontSize: '8px', color: 'var(--green)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--green)', animation: 'spill-pulse 1.1s ease infinite' }}></span>LIVE TRANSCRIPTION
                </span>
              : <span className="mono" style={{ fontSize: '8px', color: 'var(--text-3)' }}>TRANSCRIBED · SIGNED</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {LINES.map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: '9px', opacity: i < n ? 1 : 0, transform: i < n ? 'translateY(0)' : 'translateY(5px)', transition: 'all .45s var(--ease)' }}>
                <span className="mono" style={{ fontSize: '9px', fontWeight: 600, color: l.s === 'AI' ? 'var(--indigo-light)' : 'var(--green)', width: '24px', flexShrink: 0, paddingTop: '2px' }}>{l.s}</span>
                <span style={{ fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.45 }}>{l.t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT — AI insights → scorecard */}
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {live ? (
          <div style={{ flex: 1, borderRadius: 'var(--r-md)', border: '1px solid var(--border)', background: 'var(--surface)', padding: '12px', display: 'flex', flexDirection: 'column' }}>
            <span className="label" style={{ fontSize: '8px', color: 'var(--indigo-light)', marginBottom: '10px' }}>AI · LIVE</span>
            <div key={rot} style={{ animation: 'cmd-enter .5s var(--ease)' }}>
              <div className="mono" style={{ fontSize: '8px', color: 'var(--text-3)', marginBottom: '8px', letterSpacing: '0.1em' }}>{ins.label}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {ins.lines.map((t, i) => (
                  <div key={i} style={{ fontSize: '10.5px', color: 'var(--text-2)', lineHeight: 1.4, paddingLeft: '10px', borderLeft: '2px solid var(--border-hi)' }}>{t}</div>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 'auto', display: 'flex', gap: '4px', paddingTop: '12px' }}>
              {INSIGHTS.map((_, i) => (
                <span key={i} style={{ height: '3px', flex: 1, borderRadius: '2px', background: i === rot ? 'var(--indigo-light)' : 'var(--surface-raised)', transition: 'background .4s' }}></span>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, borderRadius: 'var(--r-md)', border: '1px solid var(--border)', background: 'var(--surface)', padding: '12px', display: 'flex', flexDirection: 'column', animation: 'cmd-enter .55s var(--ease)' }}>
            <span className="label" style={{ fontSize: '8px', color: 'var(--indigo-light)', marginBottom: '11px' }}>AI SCORECARD</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
              {SCORES.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: resolved ? 1 : 0, transform: resolved ? 'none' : 'translateY(4px)', transition: `all .4s var(--ease) ${i * 0.09}s` }}>
                  <span style={{ fontSize: '10px', color: 'var(--text-2)' }}>{s.k}</span>
                  <span style={{ display: 'flex', gap: '2px' }}>
                    {[0, 1, 2, 3, 4].map(d => (
                      <svg key={d} width="9" height="9" viewBox="0 0 24 24" fill={d < s.n ? 'var(--green)' : 'none'} stroke={d < s.n ? 'var(--green)' : 'var(--text-4)'} strokeWidth="2"><path d="M12 2l3 6.5 7 .9-5 4.9 1.2 7-6.4-3.4-6.4 3.4 1.2-7-5-4.9 7-.9z"/></svg>
                    ))}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 'auto', padding: '10px 11px', borderRadius: 'var(--r-sm)', background: 'var(--green-soft)', border: '1px solid color-mix(in srgb, var(--green) 30%, transparent)' }}>
              <div className="mono" style={{ fontSize: '8px', color: 'var(--text-3)', marginBottom: '5px' }}>RECOMMENDATION</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--green)' }}>Strong Hire</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* AI interviewer orb — concentric rings, brighter while speaking */
function AIOrb({ speaking }: { speaking: boolean }) {
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'relative', width: '52px', height: '52px' }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{ position: 'absolute', inset: `${i * 7}px`, borderRadius: '50%',
            border: `1.2px solid color-mix(in srgb, var(--indigo-light) ${speaking ? 70 : 38}%, transparent)`,
            animation: speaking ? `node-pulse ${1.4 + i * 0.3}s ease-in-out infinite` : 'none', transition: 'border-color .4s' }}></span>
        ))}
        <span style={{ position: 'absolute', inset: '18px', borderRadius: '50%',
          background: 'radial-gradient(circle at 40% 35%, var(--indigo-pale), var(--indigo))',
          boxShadow: speaking ? '0 0 16px 2px color-mix(in srgb, var(--indigo-light) 55%, transparent)' : '0 0 6px color-mix(in srgb, var(--indigo-light) 30%, transparent)',
          transition: 'box-shadow .4s' }}></span>
      </div>
    </div>
  );
}

/* abstract candidate avatar — head + shoulders silhouette, no facial features */
function Avatar({ speaking }: { speaking: boolean }) {
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="58" height="58" viewBox="0 0 64 64" style={{ filter: speaking ? 'drop-shadow(0 0 6px color-mix(in srgb, var(--green) 45%, transparent))' : 'none', transition: 'filter .4s' }}>
        <defs>
          <linearGradient id="av-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--text-3)" />
            <stop offset="100%" stopColor="var(--text-4)" />
          </linearGradient>
        </defs>
        <circle cx="32" cy="23" r="12" fill="url(#av-grad)" />
        <path d="M12 60 C12 44 22 38 32 38 C42 38 52 44 52 60 Z" fill="url(#av-grad)" />
        {speaking && <circle cx="32" cy="23" r="14.5" fill="none" stroke="var(--green)" strokeWidth="1" opacity="0.6" style={{ animation: 'node-pulse 1.4s ease-in-out infinite' }} />}
      </svg>
    </div>
  );
}
